import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ConfigProvider } from './context/ConfigContext'
import { LicenciaProvider, useLicencia } from './context/LicenciaContext'
import { SyncProvider } from './context/SyncContext'
import { CarritoProvider, useCarritoCtx } from './context/CarritoContext'
import { BTPrinterProvider } from './context/BTPrinterContext'
import { hayUsuarios } from './db/usuarios'
import TopBar from './components/layout/TopBar'
import BottomNav from './components/layout/BottomNav'
import Cobro from './pages/Cobro'
import Inventario from './pages/Inventario'
import Clientes from './pages/Clientes'
import Reportes from './pages/Reportes'
import Configuracion from './pages/Configuracion'
import Login from './pages/Login'
import SetupAdmin from './pages/SetupAdmin'
import Activacion from './pages/Activacion'

function UpdateChip() {
  const { needRefresh: [needRefresh] } = useRegisterSW()
  const { usuario } = useAuth()
  const navigate = useNavigate()

  if (!needRefresh || usuario?.rol !== 'admin') return null

  return (
    <button
      onClick={() => navigate('/config')}
      className="fixed top-14 right-3 z-50 flex items-center gap-2 bg-tienda-surface border border-tienda-accent/50 shadow-lg rounded-full px-3 py-1.5 active:scale-95 transition-transform"
    >
      <span className="w-2 h-2 rounded-full bg-tienda-accent animate-pulse flex-shrink-0" />
      <span className="text-tienda-accent text-xs font-semibold">Nueva versión</span>
    </button>
  )
}

function Layout({ setupPath }) {
  const { cantidadTotal } = useCarritoCtx()
  const { usuario } = useAuth()
  const esAdmin = usuario?.rol === 'admin'
  const navigate = useNavigate()

  // Navegar al destino post-onboarding una sola vez
  useEffect(() => {
    if (setupPath && setupPath !== '/') {
      navigate(setupPath, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col min-h-svh w-full relative">
      <TopBar />
      <UpdateChip />
      {/* pt-12 = TopBar height (48px), pb-20 = BottomNav height (~80px) */}
      <main className="flex-1 pt-12 pb-20 flex flex-col overflow-hidden">
        <Routes>
          <Route path="/" element={<Cobro />} />
          <Route path="/mis-ventas" element={esAdmin ? <Reportes /> : <Navigate to="/" replace />} />
          <Route path="/mis-productos" element={esAdmin ? <Inventario /> : <Navigate to="/" replace />} />
          <Route path="/pagos" element={<Clientes />} />
          <Route path="/config" element={esAdmin ? <Configuracion /> : <Navigate to="/" replace />} />
          {/* Redirecciones por compatibilidad con rutas anteriores */}
          <Route path="/reportes" element={<Navigate to="/mis-ventas" replace />} />
          <Route path="/inventario" element={<Navigate to="/mis-productos" replace />} />
          <Route path="/clientes" element={<Navigate to="/pagos" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <BottomNav cantidadCarrito={cantidadTotal} />
    </div>
  )
}

function LicenciaGuard({ children }) {
  const { estado } = useLicencia()

  if (estado === 'cargando') return null
  if (estado === 'expirado' || estado === 'vencido') return <Activacion />

  return children
}

function AuthGuard() {
  const { usuario } = useAuth()
  const [estado, setEstado] = useState('cargando')
  const [setupPath, setSetupPath] = useState(null)

  useEffect(() => {
    if (usuario) { setEstado('ok'); return }
    hayUsuarios().then(tiene => setEstado(tiene ? 'login' : 'sin-usuarios'))
  }, [usuario])

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen bg-tienda-bg flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (estado === 'sin-usuarios') {
    return (
      <SetupAdmin
        onCreado={(path) => {
          setSetupPath(path ?? '/')
          setEstado('ok')
        }}
      />
    )
  }

  if (estado === 'login') return <Login />

  return (
    <LicenciaGuard>
      <CarritoProvider>
        <BTPrinterProvider>
          <Layout setupPath={setupPath} />
        </BTPrinterProvider>
      </CarritoProvider>
    </LicenciaGuard>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfigProvider>
          <LicenciaProvider>
            <SyncProvider>
              <AuthGuard />
            </SyncProvider>
          </LicenciaProvider>
        </ConfigProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
